import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../../authContext";
import { getCustomerHistory, sendMessage } from "../../../api/login.service";
import { getInstagramMessageUsers, getInstagramUserMessages, sendInstagramHumanReply, InstagramThreadMessage } from "../../../api/instagram.service";
import {
  BusinessSetup,
  LeadState,
  emptyLead,
  applyFormFilled,
  foldChats,
  leadState,
  loadSetup,
  businessId,
  primaryKey,
  saveChannelId,
  saveSetup,
} from "./businessSetup";
import { getBusinessLeads, getLeadPipeline } from "../../../api/leads.service";
import { ChatChannel, ChatRow, ago, channelThread, initials, leadsToChats, loadAccount } from "./chats";
import CreateLeadDialog from "./CreateLeadDialog";

type Bubble = { id: string; text: string; mine: boolean; at: string };
type FilterId = "all";

export default function InboxPage() {
  const { displayPhoneNumber } = useAuth();
  const [params] = useSearchParams();
  const [setup, setSetup] = useState<BusinessSetup>(() => loadSetup());
  const [viewChannel, setViewChannel] = useState<ChatChannel>(() => {
    const requested = params.get("channel");
    return requested === "instagram" || requested === "web" ? requested : "whatsapp";
  });
  const [accountLabel, setAccountLabel] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [instagramId, setInstagramId] = useState("");
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterId>("all");
  const [stageFilter, setStageFilter] = useState("");
  const [selectedKey, setSelectedKey] = useState(params.get("lead") || "");
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [humanAgent, setHumanAgent] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [showList, setShowList] = useState(true);
  const [showInfo, setShowInfo] = useState(true);
  const [stageError, setStageError] = useState("");

  const persist = (next: BusinessSetup) => {
    saveSetup(next);
    setSetup(next);
  };

  const keepChats = (loaded: ChatRow[]) => {
    setChats(loaded);
    setSetup((current) => {
      const next = foldChats(current, loaded);
      if (next !== current) saveSetup(next);
      return next;
    });
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const account = await loadAccount(displayPhoneNumber);
      setPhoneNumberId(account.phoneNumberId);
      setBusinessPhone(account.businessPhone);
      setInstagramId(account.instagramId);
      setAccountLabel(account.businessPhone || "this business");
      const leads = await getBusinessLeads({
        whatsappBusinessId: account.businessPhone,
        instagramBusinessId: account.instagramId,
      });
      const remote = await getLeadPipeline(businessId()).catch((): null => null);
      const loaded = leadsToChats(leads);
      setChats(loaded);
      setSetup((current) => {
        const withPipeline = remote
          ? { ...current, stages: remote.stages, tags: remote.tags, fields: remote.fields }
          : current;
        const next = applyFormFilled(foldChats(withPipeline, loaded), loaded);
        if (next !== current) saveSetup(next);
        return next;
      });
    } catch {
      keepChats([]);
      setListError("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, [displayPhoneNumber]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const selected = chats.find((chat) => chat.key === selectedKey) || null;
  const selectedLead = selected ? leadState(setup, selected.key) : emptyLead();
  const threadChat = selected ? channelThread(selected, viewChannel) : null;

  const threadCustomerId = selected?.customerId || "";
  const threadCustomerNumber = selected?.customerNumber || "";
  const threadInstagram = (selected?.instagramId || "").replace(/^@/, "");

  useEffect(() => {
    const chat = selected ? channelThread(selected, viewChannel) : null;
    if (!chat) {
      setBubbles([]);
      setThreadLoading(false);
      return;
    }
    let cancelled = false;
    setThreadLoading(true);
    setSendError(null);
    setBubbles([]);
    (async () => {
      try {
        if (chat.channel === "instagram") {
          const handle = (chat.instagramId || "").replace(/^@/, "");
          let customerId = chat.customerId || "";
          if (!customerId && /^\d{8,}$/.test(handle)) customerId = handle;
          if (!customerId) {
            if (!handle) {
              if (!cancelled) setBubbles([]);
              return;
            }
            const users = await getInstagramMessageUsers(instagramId || undefined);
            const match = users.find(
              (user) => (user.username || "").replace(/^@/, "").toLowerCase() === handle.toLowerCase()
            );
            if (!match?.customerId) {
              if (!cancelled) setBubbles([]);
              return;
            }
            customerId = match.customerId;
          }
          const thread = await getInstagramUserMessages(customerId, instagramId || undefined);
          if (!cancelled) setBubbles(thread.map(toInstagramBubble));
          return;
        }
        if (chat.channel === "whatsapp" && phoneNumberId) {
          const history = await getCustomerHistory(chat.customerNumber, phoneNumberId);
          if (!cancelled) setBubbles(flattenHistory(history?.conversations || []));
        }
      } catch {
        if (!cancelled) {
          setBubbles([]);
          setSendError("Could not load this conversation.");
        }
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedKey, viewChannel, threadCustomerId, threadCustomerNumber, threadInstagram, instagramId, phoneNumberId]);

  const counts = useMemo(() => ({ all: chats.length }), [chats]);

  const visible = chats.filter((chat) => {
    const lead = leadState(setup, chat.key);
    if (stageFilter && lead.stageId !== stageFilter) return false;
    const haystack = `${chat.name} ${chat.handle} ${chat.preview}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  const stageCounts = useMemo(() => {
    const countsById: Record<string, number> = {};
    const seen = new Set<string>();
    chats.forEach((chat) => {
      const id = primaryKey(setup, chat.key);
      if (seen.has(id)) return;
      seen.add(id);
      const stageId = leadState(setup, id).stageId;
      if (!stageId) return;
      countsById[stageId] = (countsById[stageId] || 0) + 1;
    });
    return countsById;
  }, [chats, setup]);

  const patchLead = (key: string, patch: Partial<LeadState>) => {
    const id = primaryKey(setup, key);
    const current = leadState(setup, id);
    persist({
      ...setup,
      leads: { ...setup.leads, [id]: { ...current, ...patch } },
    });
  };

  const lastCustomerAt = [...bubbles].reverse().find((bubble) => !bubble.mine)?.at;
  const outsideWindow = viewChannel === "whatsapp" && threadChat && lastCustomerAt
    ? Date.now() - new Date(lastCustomerAt).getTime() > 24 * 60 * 60 * 1000
    : false;

  const send = async () => {
    if (!threadChat || !draft.trim() || sending || outsideWindow) return;
    const text = draft.trim();
    setSending(true);
    setSendError(null);
    try {
      if (threadChat.channel === "instagram" && threadChat.customerId) {
        const result = await sendInstagramHumanReply({
          recipientId: threadChat.customerId,
          text,
          businessIgId: instagramId || undefined,
          humanAgent,
        });
        if (!result?.success) {
          setSendError(result?.error || "Could not send this reply.");
          return;
        }
      } else if (threadChat.channel === "whatsapp") {
        await sendMessage(threadChat.customerNumber, text, phoneNumberId, businessPhone);
      }
      setBubbles((current) => current.concat({
        id: `local-${Date.now()}`,
        text,
        mine: true,
        at: new Date().toISOString(),
      }));
      setDraft("");
    } catch {
      setSendError("Could not send this reply.");
    } finally {
      setSending(false);
    }
  };

  const stageOf = (chat: ChatRow) => setup.stages.find((stage) => stage.id === leadState(setup, chat.key).stageId);

  return (
    <div className="flex h-[calc(100vh-88px)] min-h-[560px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 overflow-x-auto border-b bg-gray-50 px-3 py-2">
        {([
          ["all", `All Chats (${counts.all})`],
        ] as Array<[FilterId, string]>).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilter(id)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-sm ${
              filter === id ? "bg-white font-semibold text-blue-700 shadow-sm" : "text-gray-600"
            }`}
          >
            {label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setShowList((open) => !open)} className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700">
            {showList ? "Hide chats" : "Show chats"}
          </button>
          <button type="button" onClick={() => setShowInfo((open) => !open)} className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700">
            {showInfo ? "Hide info" : "Show info"}
          </button>
          <button type="button" onClick={() => setCreateOpen(true)} className="whitespace-nowrap rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white">
            Create
          </button>
        </div>
      </div>

      {setup.stages.length > 0 ? (
        <div className="flex gap-1 overflow-x-auto border-b px-3 py-2">
          {setup.stages.map((stage) => {
            const active = stageFilter === stage.id;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => setStageFilter(active ? "" : stage.id)}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-xs font-semibold text-white ${active ? "ring-2 ring-offset-1 ring-gray-900" : ""}`}
                style={{ background: stage.color }}
              >
                {stageCounts[stage.id] || 0} {stage.name}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="border-b px-3 py-2 text-sm text-gray-500">Add a stage. A message becomes a lead in the first stage.</p>
      )}

      <div className={`grid min-h-0 flex-1 grid-cols-1 ${showList && showInfo ? "lg:grid-cols-[300px_minmax(0,1fr)_280px]" : showList ? "lg:grid-cols-[300px_minmax(0,1fr)]" : showInfo ? "lg:grid-cols-[minmax(0,1fr)_280px]" : ""}`}>
        <section className={`min-h-0 flex-col border-r ${showList ? (selected ? "hidden lg:flex" : "flex") : "hidden"}`}>
          <div className="border-b px-3 py-3">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search or start a new chat"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? <p className="p-4 text-sm text-gray-500">Loading conversations...</p> : null}
            {!loading && listError ? (
              <p className="p-4 text-sm text-red-600">
                {listError}
              </p>
            ) : null}
            {!loading && !listError && visible.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No leads yet. Create one, or a person appears here when they message.</p>
            ) : null}
            {visible.filter((chat, index, list) => list.findIndex((other) => primaryKey(setup, other.key) === primaryKey(setup, chat.key)) === index).map((chat) => {
              const stage = stageOf(chat);
              const active = chat.key === selectedKey;
              return (
                <button
                  key={chat.key}
                  type="button"
                  onClick={() => { setSelectedKey(chat.key); setViewChannel(chat.channel); setStageError(""); }}
                  className={`flex w-full items-start gap-3 border-b px-3 py-3 text-left ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700">
                    {initials(chat.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-gray-900">{chat.name}</span>
                      <span className="shrink-0 text-[11px] text-gray-400">{ago(chat.at)}</span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-gray-500">{chat.preview || chat.handle}</span>
                    {stage ? (
                      <span className="mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium text-white" style={{ background: stage.color }}>
                        {stage.name}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className={`min-h-0 flex-col bg-[#f4f1ea] ${selected || !showList ? "flex" : "hidden lg:flex"}`}>
          {!selected ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
              <p className="text-lg font-semibold text-gray-800">No chat selected</p>
              <p className="mt-1 max-w-sm text-sm text-gray-500">Pick a conversation from the list to engage with your leads.</p>
            </div>
          ) : (
            <>
              <div className="border-b bg-white px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button type="button" className="text-sm text-blue-600 lg:hidden" onClick={() => { setSelectedKey(""); setStageError(""); }}>
                      Back
                    </button>
                    <div>
                      <p className="font-semibold text-gray-900">{selected.name}</p>
                      <p className="text-xs text-gray-500">{selected.handle}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setReminderOpen(true)} className="text-sm font-medium text-blue-600">
                    Reminder
                  </button>
                </div>
                <div className="mt-3 flex gap-1">
                  {(["whatsapp", "instagram", "web"] as ChatChannel[]).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setViewChannel(item)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        viewChannel === item ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {item === "web" ? "Web" : item === "whatsapp" ? "WhatsApp" : "Instagram"}
                    </button>
                  ))}
                </div>
                <p className="mt-2 truncate text-xs text-gray-500">Connected to {accountLabel || "this business"}</p>
              </div>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
                {viewChannel === "web" ? (
                  (selected.forms || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No web form for this person yet.</p>
                  ) : (
                    (selected.forms || []).map((form, index) => (
                      <div key={`${form.at}-${index}`} className="flex justify-start">
                        <div className="max-w-[75%] rounded-lg bg-white px-3 py-2 text-sm text-gray-900 shadow-sm">
                          <p className="whitespace-pre-wrap break-words">{form.text}</p>
                          <p className="mt-1 text-right text-[10px] text-gray-500">{form.at ? new Date(form.at).toLocaleString() : ""}</p>
                        </div>
                      </div>
                    ))
                  )
                ) : null}
                {viewChannel !== "web" && threadLoading ? <p className="text-sm text-gray-500">Loading conversation...</p> : null}
                {viewChannel !== "web" && !threadLoading && !threadChat ? (
                  <p className="text-sm text-gray-500">
                    {`No ${viewChannel === "whatsapp" ? "WhatsApp" : "Instagram"} conversation for this lead yet.`}
                  </p>
                ) : null}
                {viewChannel !== "web" && !threadLoading && threadChat && bubbles.length === 0 ? <p className="text-sm text-gray-500">No messages yet.</p> : null}
                {viewChannel !== "web" ? bubbles.map((bubble) => (
                  <div key={bubble.id} className={`flex ${bubble.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${bubble.mine ? "bg-[#d9fdd3] text-gray-900" : "bg-white text-gray-900"}`}>
                      <p className="whitespace-pre-wrap break-words">{bubble.text}</p>
                      <p className="mt-1 text-right text-[10px] text-gray-500">{bubble.at ? new Date(bubble.at).toLocaleString() : ""}</p>
                    </div>
                  </div>
                )) : null}
              </div>
              {viewChannel === "web" ? null : <div className="border-t bg-white p-3">
                {outsideWindow ? (
                  <p className="mb-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-700">
                    This conversation is outside the 24-hour window. An approved template is required before another plain message can be sent.
                  </p>
                ) : null}
                {threadChat?.channel === "instagram" ? (
                  <label className="mb-2 flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={humanAgent} onChange={(event) => setHumanAgent(event.target.checked)} />
                    Human Agent {humanAgent ? "(after 24 hours, up to 7 days)" : "(inside the 24-hour window)"}
                  </label>
                ) : null}
                <div className="flex items-end gap-2">
                  <textarea
                    value={draft}
                    rows={2}
                    disabled={outsideWindow || !threadChat}
                    placeholder="Type a message"
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        send();
                      }
                    }}
                    className="min-h-[44px] flex-1 resize-none rounded-2xl border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={send}
                    disabled={sending || outsideWindow || !draft.trim() || !threadChat}
                    className="rounded-2xl bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-200 disabled:text-gray-500"
                  >
                    {sending ? "Sending..." : "Send"}
                  </button>
                </div>
                {sendError ? <p className="mt-2 text-xs text-red-600">{sendError}</p> : null}
              </div>}
            </>
          )}
        </section>

        <aside className={`min-h-0 flex-col overflow-y-auto border-l bg-white ${showInfo && selected ? "hidden lg:flex" : "hidden"}`}>
          {selected ? (
            <div className="space-y-4 p-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">Personal info</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">{selected.name}</p>
                <p className="text-sm text-gray-600">{selected.handle}</p>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Same person on every channel. A WhatsApp number typed here matches a later WhatsApp chat or call.</p>
                <label className="block text-sm text-gray-700">
                  WhatsApp number
                  <input
                    key={`${selected.key}:wa:${selectedLead.whatsappNumber}`}
                    defaultValue={selectedLead.whatsappNumber}
                    onBlur={(event) => persist(saveChannelId(setup, selected.key, "whatsappNumber", event.target.value))}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  Instagram
                  <input
                    key={`${selected.key}:ig:${selectedLead.instagramId}`}
                    defaultValue={selectedLead.instagramId}
                    onBlur={(event) => persist(saveChannelId(setup, selected.key, "instagramId", event.target.value))}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  Phone
                  <input
                    key={`${selected.key}:phone:${selectedLead.phoneId}`}
                    defaultValue={selectedLead.phoneId}
                    onBlur={(event) => persist(saveChannelId(setup, selected.key, "phoneId", event.target.value))}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
                {setup.fields.map((field) => (
                  <label key={field.id} className="block text-sm text-gray-700">
                    {field.name}
                    <input
                      key={`${selected.key}:field:${field.id}:${selectedLead.attributes[field.id] || ""}`}
                      defaultValue={selectedLead.attributes[field.id] || ""}
                      onBlur={(event) => patchLead(selected.key, {
                        attributes: { ...selectedLead.attributes, [field.id]: event.target.value },
                      })}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                    />
                  </label>
                ))}
                <label className="block text-sm text-gray-700">
                  Notes
                  <textarea
                    key={`${selected.key}:notes:${selectedLead.notes}`}
                    defaultValue={selectedLead.notes}
                    rows={4}
                    placeholder="Write a note about this lead"
                    onBlur={(event) => patchLead(selected.key, { notes: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
              </div>
              <label className="flex items-center justify-between text-sm text-gray-700">
                AI auto-reply
                <input
                  type="checkbox"
                  checked={selectedLead.aiOn}
                  onChange={(event) => patchLead(selected.key, { aiOn: event.target.checked })}
                />
              </label>
              <p className="text-xs text-gray-400">For this lead. Stages below belong to this business.</p>
              <label className="block text-sm text-gray-700">
                Stage
                <select
                  value={selectedLead.stageId}
                  onChange={(event) => {
                    const nextId = event.target.value;
                    const nextStage = setup.stages.find((stage) => stage.id === nextId);
                    if (nextStage?.requireAttribute && nextStage.attributeName) {
                      const filled = (selectedLead.attributes[nextStage.attributeName] || "").trim();
                      if (!filled) {
                        setStageError(`Fill in ${nextStage.attributeName} before this lead can move to ${nextStage.name}.`);
                        return;
                      }
                    }
                    setStageError("");
                    patchLead(selected.key, { stageId: nextId });
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                >
                  {setup.stages.length === 0 ? <option value="">No stage yet</option> : null}
                  {setup.stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </select>
              </label>
              {stageError ? <p className="text-xs text-red-600">{stageError}</p> : null}
              {setup.stages.filter((stage) => stage.requireAttribute && stage.attributeName).map((stage) => (
                <label key={stage.id} className="block text-sm text-gray-700">
                  {stage.attributeName}
                  <span className="ml-1 text-xs text-gray-400">needed for {stage.name}</span>
                  <input
                    value={selectedLead.attributes[stage.attributeName] || ""}
                    onChange={(event) => patchLead(selected.key, {
                      attributes: { ...selectedLead.attributes, [stage.attributeName]: event.target.value },
                    })}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
              ))}
              {selectedLead.lastCall ? (
                <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
                  Last call · {selectedLead.lastCall.direction}
                  {selectedLead.lastCall.at ? ` · ${new Date(selectedLead.lastCall.at).toLocaleString()}` : ""}
                  {selectedLead.lastCall.durationSeconds != null ? ` · ${selectedLead.lastCall.durationSeconds}s` : ""}
                </p>
              ) : null}
              {selectedLead.reminder ? (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Reminder {new Date(selectedLead.reminder.dueAt).toLocaleString()}
                  {selectedLead.reminder.note ? ` — ${selectedLead.reminder.note}` : ""}
                </p>
              ) : null}
              {setup.scripts.length > 0 ? (
                <div className="space-y-3 border-t pt-3">
                  {setup.scripts.map((section) => {
                    const done = section.items.filter((_, index) => selectedLead.checks.includes(`${section.id}:${index}`)).length;
                    return (
                      <div key={section.id}>
                        <p className="text-sm font-medium text-gray-800">{section.name} <span className="text-xs text-gray-400">{done}/{section.items.length}</span></p>
                        <ul className="mt-1 space-y-1">
                          {section.items.map((item, index) => {
                            const checkId = `${section.id}:${index}`;
                            const checked = selectedLead.checks.includes(checkId);
                            return (
                              <li key={checkId}>
                                <label className="flex items-center gap-2 text-sm text-gray-600">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      const checks = checked
                                        ? selectedLead.checks.filter((id) => id !== checkId)
                                        : selectedLead.checks.concat(checkId);
                                      patchLead(selected.key, { checks });
                                    }}
                                  />
                                  {item}
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>

      {createOpen ? (
        <CreateLeadDialog
          businessPhone={businessPhone}
          instagramBusinessId={instagramId}
          onClose={() => setCreateOpen(false)}
          onCreated={(conversationId) => {
            setCreateOpen(false);
            setSelectedKey(`lead:${conversationId}`);
            loadList();
          }}
        />
      ) : null}
      {reminderOpen && selected ? (
        <ReminderDialog
          initialNote={selectedLead.reminder?.note || ""}
          onClose={() => setReminderOpen(false)}
          onSave={(reminder) => {
            patchLead(selected.key, { reminder });
            setReminderOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function ReminderDialog({
  initialNote,
  onClose,
  onSave,
}: {
  initialNote: string;
  onClose: () => void;
  onSave: (reminder: { dueAt: number; time: string; note: string }) => void;
}) {
  const [days, setDays] = useState(2);
  const [time, setTime] = useState("15:00");
  const [note, setNote] = useState(initialNote);

  const save = () => {
    const due = new Date();
    due.setDate(due.getDate() + days);
    const [hours, minutes] = time.split(":").map((part) => Number(part));
    due.setHours(hours || 0, minutes || 0, 0, 0);
    onSave({ dueAt: due.getTime(), time, note: note.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Next Reminder</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-xl text-gray-400">×</button>
        </div>
        <label className="block text-sm text-gray-600">
          Date
          <div className="mt-1 flex items-center gap-2">
            <input value={`In ${days} day${days === 1 ? "" : "s"}`} readOnly className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
        </label>
        <div className="mt-2 flex gap-2">
          {[1, 2, 3, 4].map((offset) => (
            <button
              key={offset}
              type="button"
              onClick={() => setDays(offset)}
              className={`rounded-md border px-3 py-1 text-sm ${days === offset ? "border-blue-600 text-blue-600" : "border-gray-200 text-gray-600"}`}
            >
              +{offset}
            </button>
          ))}
        </div>
        <label className="mt-3 block text-sm text-gray-600">
          Time
          <input type="time" value={time} onChange={(event) => setTime(event.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>
        <label className="mt-3 block text-sm text-gray-600">
          Reminder Note
          <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-600">Cancel</button>
          <button type="button" onClick={save} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">Save</button>
        </div>
      </div>
    </div>
  );
}

function toInstagramBubble(message: InstagramThreadMessage): Bubble {
  return {
    id: message.mid || `${message.timestamp}-${message.text}`,
    text: message.text || message.type || "Message",
    mine: message.direction === "outbound",
    at: message.updatedAt || message.timestamp,
  };
}

function flattenHistory(conversations: Array<{ messageId?: string; left?: Array<{ message: string; timestamp: string; messageId?: string }>; right?: Array<{ message: string; timestamp: string; messageId?: string }> }>): Bubble[] {
  const bubbles: Bubble[] = [];
  conversations.forEach((turn, turnIndex) => {
    (turn.left || []).forEach((message, index) => {
      bubbles.push({
        id: message.messageId || `l-${turnIndex}-${index}`,
        text: message.message,
        mine: false,
        at: message.timestamp,
      });
    });
    (turn.right || []).forEach((message, index) => {
      bubbles.push({
        id: message.messageId || `r-${turnIndex}-${index}`,
        text: message.message,
        mine: true,
        at: message.timestamp,
      });
    });
  });
  return bubbles;
}
